import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import {
  HappyIcon,
  MehIcon,
  UnhappyIcon
} from '../../../components/icons/RatingIcons';
import MessageRating from '../../../types/MessageRating';
import ReviewTag from '../../../types/ReviewTag';
import Issue from '../../../types/Issue';
import AnalyticsService from '../../../services/AnalyticsService/AnalyticsService';
import useAuthRoles from '../../../hooks/useAuthRoles';
import TagManager from '../TagManager/TagManager';
import CommentArea from '../CommentArea/CommentArea';
import IssueManager from '../IssueManager/IssueManager';
import { MessageReviewUpdate } from '../ChatMessageContainer/ChatMessageContainer';

interface RatingContainerProps {
  reviewerRating: MessageRating | null;
  userRating: MessageRating | null;
  messageId: number;
  chatbotId: number;
  reviewTag?: ReviewTag | null;
  reviewComment?: string | null;
  issue?: Issue | null;
  setError: React.Dispatch<React.SetStateAction<string>>;
  onReviewUpdate?: (update: MessageReviewUpdate) => void;
}

function RatingContainer({
  reviewerRating,
  userRating,
  messageId,
  chatbotId,
  reviewTag = null,
  reviewComment = null,
  issue = null,
  setError,
  onReviewUpdate
}: Readonly<RatingContainerProps>): React.ReactElement {
  const { userIsJapetoAdmin } = useAuthRoles();

  const [currentRating, setCurrentRating] = useState<MessageRating | null>(
    reviewerRating || null
  );
  const [currentTag, setCurrentTag] = useState<ReviewTag | null>(
    reviewTag ?? null
  );
  const [currentComment, setCurrentComment] = useState<string | null>(
    reviewComment
  );
  const [currentIssue, setCurrentIssue] = useState<Issue | null>(issue ?? null);

  useEffect(() => {
    setCurrentRating(reviewerRating || null);
  }, [reviewerRating]);

  useEffect(() => {
    setCurrentTag(reviewTag ?? null);
  }, [reviewTag]);

  useEffect(() => {
    setCurrentComment(reviewComment);
  }, [reviewComment]);

  useEffect(() => {
    setCurrentIssue(issue ?? null);
  }, [issue]);

  const handleRateMessage = (messageRating: MessageRating): void => {
    setError('');
    AnalyticsService.rateMessage(chatbotId, messageId, messageRating).catch(
      (err) => {
        if (err.name === 'CanceledError') return;
        const error =
          err.apiError?.message ||
          'Something went wrong. Please try again later.';
        setError(error);
      }
    );
  };

  const handleUndoRateMessage = (): void => {
    setError('');
    AnalyticsService.undoRateMessage(chatbotId, messageId).catch((err) => {
      if (err.name === 'CanceledError') return;
      const error =
        err.apiError?.message ||
        'Something went wrong. Please try again later.';
      setError(error);
    });
  };

  const handleRatingClick = (rating: MessageRating): void => {
    if (currentRating === rating) {
      setCurrentRating(null);
      onReviewUpdate?.({ rating: null });
      handleUndoRateMessage();
    } else {
      setCurrentRating(rating);
      onReviewUpdate?.({ rating });
      handleRateMessage(rating);
    }
  };

  const showUserRating = (): React.ReactNode => {
    if (userRating) {
      let ratingText = 'rated this message';
      if (userRating === 'GOOD') {
        ratingText = 'liked this message';
      } else if (userRating === 'OK') {
        ratingText = 'found this message okay';
      } else if (userRating === 'BAD') {
        ratingText = 'disliked this message';
      }
      return (
        <p className='text-text-secondary mt-1.5 text-xs'>User {ratingText}</p>
      );
    }
    return null;
  };

  const showTagsAndComments =
    userIsJapetoAdmin() && (currentRating === 'OK' || currentRating === 'BAD');

  return (
    <div>
      {showUserRating()}

      {userIsJapetoAdmin() && (
        <div className='flex justify-start gap-2 pt-1.5'>
          <Button
            variant='ghost'
            size='icon'
            className='h-auto w-auto p-1'
            onClick={() => handleRatingClick('GOOD')}
            aria-label="Rate message 'happy'"
            title='Happy'
          >
            <HappyIcon isSelected={currentRating === 'GOOD'} />
          </Button>
          <Button
            variant='ghost'
            size='icon'
            className='h-auto w-auto p-1'
            onClick={() => handleRatingClick('OK')}
            aria-label="Rate message 'meh'"
            title='Meh'
          >
            <MehIcon isSelected={currentRating === 'OK'} />
          </Button>
          <Button
            variant='ghost'
            size='icon'
            className='h-auto w-auto p-1'
            onClick={() => handleRatingClick('BAD')}
            aria-label="Rate message 'unhappy'"
            title='Unhappy'
          >
            <UnhappyIcon isSelected={currentRating === 'BAD'} />
          </Button>
        </div>
      )}

      {showTagsAndComments && (
        <div className='mt-2 flex w-full flex-col gap-3 sm:w-[350px]'>
          <IssueManager
            chatbotId={chatbotId}
            messageId={messageId}
            initialIssue={currentIssue}
            onIssueChange={(updatedIssue) => {
              setCurrentIssue(updatedIssue);
              onReviewUpdate?.({ issue: updatedIssue });
            }}
            setError={setError}
          />
          <TagManager
            chatbotId={chatbotId}
            messageId={messageId}
            initialTag={currentTag}
            onTagChange={(tag) => {
              setCurrentTag(tag);
              onReviewUpdate?.({ tag });
            }}
            setError={setError}
          />
          <CommentArea
            chatbotId={chatbotId}
            messageId={messageId}
            initialComment={currentComment}
            onCommentChange={(comment) => {
              setCurrentComment(comment);
              onReviewUpdate?.({ comment });
            }}
            setError={setError}
          />
        </div>
      )}
    </div>
  );
}

export default RatingContainer;
